<template>
  <div
    class="product-gallery"
    :class="{
      'product-gallery--center-right': centerRight(),
      'product-gallery--has-video': productDetails.videoViewmodels.length,
      'product-gallery--has-many-videos': productDetails.videoViewmodels.length > 1,
      'product-gallery--has-too-much-items': carouselItems.length > 3,
      'product-gallery--thumb-slider': carouselItems.length > 4,
      'product-gallery--single-item': carouselItems.length === 1,
      'product-gallery--three-view': isThreePicView,
      'product-detailed-page--no-scale': isFallbackImage || productDetails.isSet
    }"
    role="img"
  >
    <carousel
      class="product-gallery__big-images"
      v-if="!isThreePicView"
      :slides-count="pictureCount"
      :slides-per-view-desktop="1"
      :slides-per-view-mobile="1"
      :slides-per-view-tablet="1"
      :show-pagination="true"
      :thumbnails="thumbnails"
      :options="{
        spaceBetween: 8
      }"
      @change="onCarouselChange"
      ref="carouselRef"
    >
      <template
        v-for="(item, index) in carouselItems"
        :key="`product-gallery-${index}`"
        #[`slide-${index}`]
      >
        <div
          :class="`product-gallery__image-wrapper slide-${index}`"
          @click="imageClickHandler(index)"
        >
          <template
            v-if="item.videoId"
          >
            <div
              class="product-gallery__video"
            >
              <video-player
                :video-data="item"
                ref="videoPlayersRef"
                @play-from-start="trackVideoPlay(item)"
              />
            </div>
          </template>
          <template
            v-else
          >
            <component :is="index === 0 ? 'div' : 'lazy-load'">
              <image-element
                class="product-gallery__picture"
                :class="{'product-gallery__picture--no-zoom': !enableZoom}"
                :picture-class="imageClass"
                img-class="product-gallery__image product-gallery__image--auto-center"
                :src="item.bigRetina ? [item.big, item.bigRetina] : [item.big]"
                :src-mobile="item.bigRetina ? [item.bigMobile, item.bigRetinaMobile] : [item.bigMobile]"
                :alt="productDetails.product.name"
                :loading="index === 0 ? 'eager' : 'lazy'"
                :webp="true"
              />
            </component>
          </template>
        </div>
      </template>
    </carousel>

    <carousel
      class="carousel__thumbnail-slider-wrapper"
      v-if="thumbnails && thumbnails.length > 4 && !isThreePicView"
      :slides-count="thumbnails.length"
      :slides-per-view-desktop="4"
      :slides-per-view-mobile="4"
      :slides-per-view-tablet="4"
      :show-pagination="false"
      :options="{
        spaceBetween: 5,
        slidesPerView: 4,
        slidesPerGroup: 1,
        centeredSlides: true,
        speed: 300,
      }"
      ref="carouselThumbRef"
      @change="onCarouselChange"
    >
      <template
        v-for="(thumb, index) in thumbnails"
        :key="`${index}-thumb`"
        #[`slide-${index}`]
      >
        <div
          :class="`carousel__thumbnail-button slide-${index}`"
          @click="thumbClickHandler(index)"
        >
          <div>
            <span class="image-element">
              <img
                :src="thumb.src"
                :alt="thumb.name"
                width="70"
                height="70"
                loading="eager"
                class="image-element__img"
              >
            </span>
          </div>
        </div>
      </template>
    </carousel>

    <template
      v-if="isThreePicView"
    >
      <div class="carousel product-gallery__big-images">
        <div class="carousel__wrapper">
          <div class="carousel__container">
            <div class="carousel__slide carousel__slide--active">
              <div class="product-gallery__image-wrapper slide-0">
                <div>
                  <image-element
                    class="product-gallery__picture"
                    :class="{'product-gallery__picture--no-zoom': !enableZoom}"
                    :picture-class="imageClass"
                    img-class="product-gallery__image product-gallery__image--auto-center"
                    :src="carouselItems[0].bigRetina ? [carouselItems[0].big, carouselItems[0].bigRetina] : [carouselItems[0].big]"
                    :src-mobile="carouselItems[0].bigRetina ? [carouselItems[0].bigMobile, carouselItems[0].bigRetinaMobile] : [carouselItems[0].bigMobile]"
                    :alt="productDetails.product.name"
                    loading="eager"
                    :webp="true"
                    @click="imageClickHandler(0)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="product-gallery__picture--three-view-wrapper">
        <image-element
          v-for="(thumb, index) in slicedThreePicImages"
          :key="`${index}-thumb`"
          class="product-gallery__picture product-gallery__picture--three-view"
          :class="[{'product-gallery__picture--no-zoom': !enableZoom}, `product-gallery__picture--three-view-${index}`]"
          :picture-class="imageClass"
          img-class="product-gallery__image product-gallery__image--auto-center"
          :src="thumb.bigRetina ? [thumb.big, thumb.bigRetina] : [thumb.big]"
          :src-mobile="thumb.bigRetina ? [thumb.bigMobile, thumb.bigRetinaMobile] : [thumb.bigMobile]"
          :alt="productDetails.product.name"
          loading="lazy"
          :webp="true"
        />
      </div>
    </template>

    <div
      class="product-gallery__video-play-btn"
      v-if="productDetails.videoViewmodels.length"
      @click="playVideo()"
    >
      <span class="product-gallery__video-play-btn-text">
        {{ $t(productDetails.videoViewmodels.length > 1 ? 'ads.ads_top_info.playVideo.many' : 'ads.ads_top_info.playVideo') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
  import {Vue, Options, Prop, Ref} from 'vue-property-decorator';
  import {defineAsyncComponent} from 'vue';
  import pigeon from '../../../bps/utilities/js/pigeon/pigeon.js';
  import tracking from '../../../bps/utilities/js/tracking/Tracking';
  import carousel from '../../../bps/components/carousel/carousel.vue';
  import productStore, {ProductStore} from '../../../bps/stores/product/ProductStore';
  import type ProductDetails from '../../../bps/types/ProductDetails';
  import vimeoService from '../../../bps/services/vimeo/VimeoService';
  import type VideoViewmodel from '../../../bps/types/VideoViewmodel';
  import type VideoPlayerType from '../../../bps/components/videoPlayer/videoPlayer.vue';
  import LazyLoad from '../../../bps/components/lazyLoad/lazyLoad.vue';
  import ImageElement from '../../../bps/components/imageElement/imageElement.vue';
  import diContainer from '../../../bps/utilities/js/diContainer/diContainer';
  import {ProductDetailsSettings} from '../../../bps/components/productDetails/productDetailsSettings';
  import {usePlaceholder} from '../../../bps/utilities/vueMixins/placeholderMixin/placeholder';

  const VideoPlayer = defineAsyncComponent(() => import('../../../bps/components/videoPlayer/videoPlayer.vue'));

  @Options({
    name: 'product-gallery',
    components: {
      ImageElement,
      LazyLoad,
      VideoPlayer,
      carousel,
    }
  })
  export default class ProductGallery extends Vue {
    @Prop()
    fullScreenGalleryId: string;

    @Prop({default: true})
    showThumbs: boolean;

    @Prop({default: true})
    enableZoom: boolean;

    @Prop()
    numberOfPictures: number;

    @Prop({default: false})
    isDetailPage: boolean;

    productStore: ProductStore = productStore;
    settings: ProductDetailsSettings = diContainer.get('productDetailsSettings');

    @Ref()
    readonly carouselRef: any;
    @Ref()
    readonly carouselThumbRef: any;
    @Ref()
    readonly videoPlayersRef: VideoPlayerType[];

    get isBottle() {
      return this.productStore.productDetails.product.price.volumeUnit === 'BOTTLE' && !this.isFallbackImage;
    }

    get imageClass() {
      if (this.isBottle) {
        return 'product-image--detail-shadow-bottle';
      } else if (this.productDetails.isSet) {
        return 'product-image--detail-shadow-set';
      }
      return '';
    }

    get isFallbackImage() {
      if (this.productDetails.images) {
        return this.productDetails.images[0].big.includes('fallback');
      }
      return false;
    }

    get productDetails(): ProductDetails {
      return this.productStore.productDetails;
    }

    get pictureCount(): number {
      return this.numberOfPictures
        ? this.numberOfPictures
        : this.productDetails.images.length + this.productDetails.videoViewmodels.length;
    }

    get carouselItems() {
      return [...this.productDetails.images, ...this.productDetails.videoViewmodels]
    }

    mounted() {
      usePlaceholder('product-gallery');
      pigeon.subscribe('productGallery:playVideo', () => this.playVideo(false));
    }

    centerRight() {
      return !this.productDetails.isSet &&
        this.productDetails.quickfacts &&
        this.productDetails.quickfacts.length > 0;
    }

    imageClickHandler(index) {
      if (this.enableZoom && this.productStore.selectedAssetType === 'image') {
        pigeon.publish('productgallery:imageclicked', {id: this.fullScreenGalleryId, showImage: index});
      }
    }

    thumbClickHandler(index) {
      this.onCarouselChange(index);
    }

    get thumbnails() {
      const thumbnails = [];

      if (this.showThumbs) {
        const lengthImages = this.productDetails.images.length;
        const lengthVideos = this.productDetails.videoViewmodels.length;
        if (lengthImages + lengthVideos <= 1) {
          return null;
        }
        for (let i = 0; i < lengthImages; i += 1) {
          thumbnails.push({
            src: this.productDetails.images[i].thumbnail,
            name: this.productDetails.product.name
          });
        }
        for (let i = 0; i < lengthVideos; i += 1) {
          thumbnails.push({
            src: vimeoService.getThmbailWithIconUrl(this.productDetails.videoViewmodels[i].thumbnailId, 201, 151),
            name: this.productDetails.product.name
          });
        }
      }

      return thumbnails;
    }

    get slicedThreePicImages() {
      return this.productDetails.images.slice(1, 3);
    }

    get isThreePicView() {
      return this.isDetailPage && !this.productDetails.isSet && this.productDetails.images.length >= 3;
    }

    onCarouselChange(slideIndex: number) {
      const assetType = 'videoId' in this.carouselItems[slideIndex] ? 'video' : 'image';
      this.productStore.selectedAssetType = assetType;
      pigeon.publish('productGallery:assetChange', assetType);
      if (this.videoPlayersRef?.length > 0) {
        this.stopAllPlayers();
      }

      if (this.carouselRef && this.carouselThumbRef) {
        this.carouselRef.changeSlide(slideIndex);
        this.carouselThumbRef.changeSlide(slideIndex);
      }
      const productWrapperEl = document.querySelector('.product-detailed-page__gallery-wrapper')
      if (assetType === 'video') {
        if (productWrapperEl) {
          productWrapperEl.classList.add('product-detailed-page__gallery-wrapper--has-video')
        }
        this.trackVideoImpression(this.carouselItems[slideIndex] as VideoViewmodel);
      } else {
        if (productWrapperEl) {
          productWrapperEl.classList.remove('product-detailed-page__gallery-wrapper--has-video')
        }
      }
    }

    playVideo(autoStart = true) {
      if (this.productDetails.videoViewmodels.length) {
        this.carouselRef.changeSlide(this.productDetails.images.length);
        if (autoStart) {
          this.videoPlayersRef[0].startPlayer();
        }
      }
    }

    stopAllPlayers() {
      if (this.productDetails.videoViewmodels.length) {
        this.videoPlayersRef.forEach((player) => {
          if (player.player) {
            player.player.pause();
          }
        })
      }
    }

    trackVideoPlay(videoData: VideoViewmodel) {
      tracking.fireVideoPlay(videoData, 'detail.main', this.productDetails.product.name);
    }

    trackVideoImpression(videoData: VideoViewmodel) {
      tracking.fireVideoImpression(videoData, 'detail.main', this.productDetails.product.name);
    }
  }
</script>
